import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AsyncBoundary, Icon } from '@kollez/design-system';
import { apiGet } from '@/lib/api';
import { formatIndianDate } from '@kollez/shared';

/**
 * /platform/colleges/:id — AISHE college detail with Kollez sibling links.
 *
 * Backed by GET /platform/colleges/:id which merges the AISHE master row
 * with (a) any Kollez `College` docs claiming the same aisheCode, (b) any
 * `Tenant` rows that adopted the code, and (c) all `Affiliation` edges on
 * matching colleges. Every read writes an AuditLog row (cross-tenant read).
 */

interface AisheMaster {
  _id: string;
  aisheCode: string;
  name: string;
  city?: string;
  state?: string;
  type?: string;
  affiliatedUniversity?: string;
  university?: string;
  yearEstablished?: number;
  autonomous?: boolean;
  minority?: boolean;
  district?: string;
  managementType?: string;
}

interface LinkedCollege { _id: string; name: string; tenant?: string; createdAt?: string; affiliatedUniversity?: string }
interface LinkedTenant { _id: string; slug: string; name: string; status?: string }
interface LinkedAffiliation {
  _id: string;
  college: string;
  university?: { _id: string; name: string; aisheCode?: string } | string;
  status?: string;
  kind?: string;
  startAt?: string;
  endAt?: string;
}

interface Payload {
  master: AisheMaster;
  linked: {
    colleges: LinkedCollege[];
    tenants: LinkedTenant[];
    affiliations: LinkedAffiliation[];
  };
}

export function PlatformCollegeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const query = useQuery({
    queryKey: ['platform', 'college-detail', id],
    queryFn: () => apiGet<Payload>(`/platform/colleges/${id}`),
    enabled: !!id,
  });

  const master = query.data?.master;
  const linked = query.data?.linked;

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow"><Link to="/platform/colleges">AISHE master</Link> · <span className="cell-sub">{master?.aisheCode ?? '—'}</span></p>
          <h1 className="h-page">{master?.name ?? 'Loading…'}</h1>
          <p className="subhead">
            {master
              ? [master.type, master.city, master.state].filter(Boolean).join(' · ') || 'AISHE master row'
              : 'National reference row'}
          </p>
        </div>
      </div>

      <AsyncBoundary kind="detail" loading={query.isLoading} error={query.error} empty={!!(query.data && !master)} emptyTitle="Not found in AISHE master">
        {master && linked && (
          <>
            <section className="card">
              <div className="card-head"><h2 className="h-section">AISHE record</h2></div>
              <div className="tbl-wrap">
                <dl className="dl">
                  <dt>AISHE code</dt><dd>{master.aisheCode}</dd>
                  <dt>Name</dt><dd>{master.name}</dd>
                  <dt>Type</dt><dd>{master.type ?? '—'}</dd>
                  <dt>Management</dt><dd>{master.managementType ?? '—'}</dd>
                  <dt>City</dt><dd>{master.city ?? '—'}</dd>
                  <dt>District</dt><dd>{master.district ?? '—'}</dd>
                  <dt>State</dt><dd>{master.state ?? '—'}</dd>
                  <dt>Year established</dt><dd>{master.yearEstablished ?? '—'}</dd>
                  <dt>Affiliated to</dt><dd>{master.university ?? master.affiliatedUniversity ?? (master.autonomous ? 'Autonomous' : '—')}</dd>
                  <dt>Minority</dt><dd>{master.minority ? 'Yes' : 'No'}</dd>
                </dl>
              </div>
            </section>

            <section className="card">
              <div className="card-head">
                <h2 className="h-section">Kollez tenants using this AISHE code</h2>
                <span className="cell-sub">{linked.tenants.length} matched</span>
              </div>
              {linked.tenants.length === 0 ? (
                <p className="helper p-4 m-0">No Kollez tenant claims this AISHE code yet.</p>
              ) : (
                <div className="tbl-wrap">
                  <table className="tbl">
                    <thead><tr><th>Name</th><th>Slug</th><th>Status</th><th></th></tr></thead>
                    <tbody>
                      {linked.tenants.map((t) => (
                        <tr key={t._id}>
                          <td className="cell-title">{t.name}</td>
                          <td className="cell-sub">{t.slug}</td>
                          <td><span className="badge">{t.status ?? '—'}</span></td>
                          <td><Link className="btn btn-outline btn-sm" to={`/platform/tenants/${t._id}`}><Icon name="external-link" size={12} />Open tenant</Link></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section className="card">
              <div className="card-head">
                <h2 className="h-section">College docs on this code</h2>
                <span className="cell-sub">{linked.colleges.length} matched</span>
              </div>
              {linked.colleges.length === 0 ? (
                <p className="helper p-4 m-0">No `College` document points at this AISHE code.</p>
              ) : (
                <div className="tbl-wrap">
                  <table className="tbl">
                    <thead><tr><th>Name</th><th>Created</th><th>Affiliated</th></tr></thead>
                    <tbody>
                      {linked.colleges.map((c) => (
                        <tr key={c._id}>
                          <td className="cell-title">{c.name}</td>
                          <td className="cell-sub">{c.createdAt ? formatIndianDate(new Date(c.createdAt)) : '—'}</td>
                          <td className="cell-sub">{c.affiliatedUniversity ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section className="card">
              <div className="card-head">
                <h2 className="h-section">Affiliations</h2>
                <span className="cell-sub">{linked.affiliations.length} rows</span>
              </div>
              {linked.affiliations.length === 0 ? (
                <p className="helper p-4 m-0">No affiliations recorded on the linked colleges.</p>
              ) : (
                <div className="tbl-wrap">
                  <table className="tbl">
                    <thead><tr><th>University</th><th>Kind</th><th>Status</th><th>From</th><th>To</th></tr></thead>
                    <tbody>
                      {linked.affiliations.map((a) => {
                        const uni = typeof a.university === 'object' && a.university !== null ? a.university.name : '—';
                        return (
                          <tr key={a._id}>
                            <td className="cell-title">{uni}</td>
                            <td>{a.kind ?? '—'}</td>
                            <td>{a.status ?? '—'}</td>
                            <td className="cell-sub">{a.startAt ? formatIndianDate(new Date(a.startAt)) : '—'}</td>
                            <td className="cell-sub">{a.endAt ? formatIndianDate(new Date(a.endAt)) : 'ongoing'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}
      </AsyncBoundary>
    </div>
  );
}
